import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowUpRight, Instagram, X } from "lucide-react";
import Reveal from "../Reveal";
import { work } from "../../data/site";

export default function Work() {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (!active) return;
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active]);

  return (
    <section
      id="work"
      data-testid="work-section"
      className="relative py-24 md:py-32 border-t border-[#2E2E38]"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 left-1/4 w-[600px] h-[600px] rounded-full opacity-20 blur-3xl"
        style={{ background: "radial-gradient(circle, #4A1D6E 0%, transparent 70%)" }}
      />

      <div className="relative max-w-[1600px] mx-auto px-6 lg:px-10">
        <Reveal>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
            <div className="md:col-span-4">
              <p className="font-['JetBrains_Mono'] text-xs uppercase tracking-[0.25em] text-[#E3FF34]">
                [ Selected work — 2024/25 ]
              </p>
            </div>
            <div className="md:col-span-8">
              <h2
                data-testid="work-heading"
                className="font-['Outfit'] font-medium tracking-tight text-4xl md:text-5xl lg:text-6xl text-[#F2F2F2]"
              >
                Brands we&apos;ve helped{" "}
                <span className="text-[#A3A3A3]">grow, ship, and scale.</span>
              </h2>
              <p className="mt-6 max-w-2xl font-['Outfit'] font-light text-[#A3A3A3] text-base md:text-lg leading-relaxed">
                Real client websites — captured live. Tap a card to preview the full
                site, or jump straight to it.
              </p>
            </div>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 lg:auto-rows-[260px] gap-4 md:gap-5">
          {work.map((w, i) => (
            <Reveal key={w.slug} delay={(i % 6) * 0.05}>
              <Card item={w} onOpen={() => setActive(w)} />
            </Reveal>
          ))}
        </div>
      </div>

      {active &&
        createPortal(
          <Lightbox item={active} onClose={() => setActive(null)} />,
          document.body
        )}
    </section>
  );
}

function Card({ item, onOpen }) {
  const cardRef = useRef(null);
  const handleKey = (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onOpen();
    }
  };
  const handleMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    const ry = (x - 0.5) * 6; // max ±3deg
    const rx = -(y - 0.5) * 6;
    el.style.setProperty("--rx", `${rx}deg`);
    el.style.setProperty("--ry", `${ry}deg`);
  };
  const handleLeave = () => {
    const el = cardRef.current;
    if (!el) return;
    el.style.setProperty("--rx", "0deg");
    el.style.setProperty("--ry", "0deg");
  };
  return (
    <div
      ref={cardRef}
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={handleKey}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      data-testid={`work-card-${item.slug}`}
      aria-label={`Preview ${item.name}`}
      className={`tilt-card group relative block w-full text-left h-full ${item.span} bg-[#131319] border border-[#2E2E38] overflow-hidden hover:border-[#7C3AED]/70 hover:shadow-[0_24px_60px_-20px_rgba(124,58,237,0.5)] transition-colors duration-500 focus:outline-none focus-visible:border-[#E3FF34] cursor-pointer`}
    >
      <div className="absolute inset-0">
        <img
          src={item.img}
          alt={`${item.name} screenshot`}
          loading="lazy"
          className="w-full h-full object-cover object-top opacity-90 group-hover:opacity-100 group-hover:scale-[1.04] transition-all duration-700 ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#2A0A3D]/30 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>

      <div className="relative z-10 flex items-center justify-between p-5 md:p-6">
        <span
          data-testid={`work-tag-${item.slug}`}
          className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.25em] text-[#F2F2F2] bg-[#050505]/70 backdrop-blur-sm border border-[#262626] px-2.5 py-1"
        >
          {item.industry}
        </span>
        <div className="flex items-center gap-2">
          {item.instagram && (
            <a
              href={item.instagram}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              data-testid={`work-ig-${item.slug}`}
              aria-label={`${item.name} Instagram`}
              className="w-8 h-8 inline-flex items-center justify-center bg-[#050505]/70 backdrop-blur-sm border border-[#262626] text-[#F2F2F2] hover:text-[#E3FF34] hover:border-[#E3FF34]/60 transition-colors"
            >
              <Instagram size={14} />
            </a>
          )}
          <span
            aria-hidden
            className="w-8 h-8 inline-flex items-center justify-center bg-[#050505]/70 backdrop-blur-sm border border-[#262626] text-[#F2F2F2] group-hover:text-[#E3FF34] group-hover:border-[#E3FF34]/60 transition-colors"
          >
            <ArrowUpRight size={14} />
          </span>
        </div>
      </div>

      <div className="absolute left-0 right-0 bottom-0 z-10 p-5 md:p-6">
        <h3 className="font-['Outfit'] text-xl md:text-2xl text-[#F2F2F2] tracking-tight">
          {item.name}
        </h3>
        <p className="mt-1.5 font-['Outfit'] font-light text-[#A3A3A3] text-sm md:text-[15px] max-w-md leading-relaxed">
          {item.note}
        </p>
        <div className="mt-3 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.25em] text-[#E3FF34] flex items-center gap-2">
          Preview
          <span aria-hidden>↗</span>
        </div>
      </div>
    </div>
  );
}

function Lightbox({ item, onClose }) {
  return (
    <div
      data-testid="work-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`${item.name} preview`}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 md:p-8 lightbox-fade"
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="lightbox-backdrop"
        aria-label="Close preview"
        className="absolute inset-0 bg-[#050505]/90 backdrop-blur-sm cursor-default"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-50"
        style={{
          background:
            "radial-gradient(circle at 30% 20%, rgba(74,29,110,0.35) 0%, transparent 50%), radial-gradient(circle at 70% 80%, rgba(124,58,237,0.18) 0%, transparent 55%)",
        }}
      />

      <div className="relative w-full max-w-6xl max-h-[92vh] flex flex-col bg-[#0A0A0A] border border-[#262626] shadow-[0_0_80px_-20px_rgba(124,58,237,0.4)] lightbox-pop">
        <div className="flex items-center justify-between gap-4 px-5 md:px-7 py-4 border-b border-[#262626]">
          <div className="flex items-center gap-3 min-w-0">
            <span className="hidden sm:inline-flex items-center gap-1.5 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3]">
              <span className="w-1.5 h-1.5 bg-[#7C3AED] inline-block" />
              Live
            </span>
            <span className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3] border border-[#262626] px-2 py-1">
              {item.industry}
            </span>
            <h3 className="font-['Outfit'] text-lg md:text-xl text-[#F2F2F2] tracking-tight truncate">
              {item.name}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            {item.instagram && (
              <a
                href={item.instagram}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="lightbox-ig"
                aria-label={`${item.name} Instagram`}
                className="hidden sm:inline-flex w-9 h-9 items-center justify-center border border-[#262626] text-[#F2F2F2] hover:text-[#E3FF34] hover:border-[#E3FF34]/60 transition-colors"
              >
                <Instagram size={14} />
              </a>
            )}
            <a
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="lightbox-visit"
              className="inline-flex items-center gap-2 font-['JetBrains_Mono'] text-[11px] uppercase tracking-[0.2em] px-4 py-2.5 bg-[#E3FF34] text-[#050505] hover:bg-[#CBE815] transition-colors"
            >
              Visit live
              <ArrowUpRight size={13} />
            </a>
            <button
              type="button"
              onClick={onClose}
              data-testid="lightbox-close"
              aria-label="Close preview"
              className="w-9 h-9 inline-flex items-center justify-center border border-[#262626] text-[#F2F2F2] hover:text-[#E3FF34] hover:border-[#E3FF34]/60 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="relative flex-1 overflow-y-auto bg-[#050505]">
          <img
            src={item.img}
            alt={`${item.name} full screenshot`}
            data-testid="lightbox-image"
            className="w-full h-auto block"
          />
        </div>

        <div className="hidden md:flex items-center justify-between gap-4 px-7 py-3 border-t border-[#262626] font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.25em] text-[#A3A3A3]">
          <span>Scroll to view the full page</span>
          <span className="truncate text-[#7C3AED]/80">{item.url}</span>
        </div>
      </div>
    </div>
  );
}
